import { useState, useEffect, useCallback } from 'react';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'sonner';
import { 
  ShoppingBag, 
  Plus, 
  Trash2, 
  Edit2,
  Gift,
  Tag,
  ArrowRight,
  Percent,
  Calendar,
  Clock,
  CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../../../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OPTIONS = [
  { value: 'monday', label: 'Lundi' },
  { value: 'tuesday', label: 'Mardi' },
  { value: 'wednesday', label: 'Mercredi' },
  { value: 'thursday', label: 'Jeudi' },
  { value: 'friday', label: 'Vendredi' },
  { value: 'saturday', label: 'Samedi' },
  { value: 'sunday', label: 'Dimanche' },
];

export default function BogoTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [offers, setOffers] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Modal state
  const [offerModal, setOfferModal] = useState({ open: false, data: null, isEdit: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, offerId: null, offerName: '' });

  const fetchOffers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bogo/offers`);
      setOffers(response.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des offres');
    }
  }, []);

  const fetchDishesAndCategories = useCallback(async () => {
    try {
      const [dishesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/restaurant-dashboard/menu/dishes`),
        axios.get(`${API}/restaurant-dashboard/menu/categories`)
      ]);
      setDishes(dishesRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (err) {
      console.error('Error fetching dishes/categories:', err);
    }
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([fetchOffers(), fetchDishesAndCategories()]);
      setLoading(false);
    };
    fetchAll();
  }, [fetchOffers, fetchDishesAndCategories]);

  const openOfferModal = (offer = null) => {
    if (offer) {
      setOfferModal({
        open: true,
        isEdit: true,
        data: { ...offer }
      });
    } else {
      setOfferModal({
        open: true,
        isEdit: false,
        data: {
          name: '',
          description: '',
          is_active: true,
          buy_type: 'product',
          buy_product_id: '',
          buy_product_name: '',
          buy_category_id: '',
          buy_category_name: '',
          buy_quantity: 3,
          get_type: 'same',
          get_product_id: '',
          get_product_name: '',
          get_quantity: 1,
          get_discount_type: 'free',
          get_discount_value: null,
          start_date: '',
          end_date: '',
          max_uses_per_order: 1,
          available_days: null,
          start_time: '',
          end_time: ''
        }
      });
    }
  };

  const handleBuyProductSelect = (productId) => {
    const product = dishes.find(d => d.id === productId);
    if (product) {
      setOfferModal({
        ...offerModal,
        data: {
          ...offerModal.data,
          buy_product_id: productId,
          buy_product_name: product.name
        }
      });
    }
  };

  const handleBuyCategorySelect = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (category) {
      setOfferModal({
        ...offerModal,
        data: {
          ...offerModal.data,
          buy_category_id: categoryId,
          buy_category_name: category.name
        }
      });
    }
  };

  const handleGetProductSelect = (productId) => {
    const product = dishes.find(d => d.id === productId);
    if (product) {
      setOfferModal({
        ...offerModal,
        data: {
          ...offerModal.data,
          get_product_id: productId,
          get_product_name: product.name
        }
      });
    }
  };

  const handleDayToggle = (dayValue) => {
    const currentDays = offerModal.data.available_days || [];
    let newDays;
    if (currentDays.includes(dayValue)) {
      newDays = currentDays.filter(d => d !== dayValue);
    } else {
      newDays = [...currentDays, dayValue];
    }
    setOfferModal({
      ...offerModal,
      data: { ...offerModal.data, available_days: newDays.length > 0 ? newDays : null }
    });
  };

  const handleSaveOffer = async () => {
    const data = offerModal.data;
    
    if (!data.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    if (data.buy_type === 'product' && !data.buy_product_id) {
      toast.error('Veuillez sélectionner un produit à acheter');
      return;
    }
    if (data.buy_type === 'category' && !data.buy_category_id) {
      toast.error('Veuillez sélectionner une catégorie');
      return;
    }
    if (data.get_type === 'product' && !data.get_product_id) {
      toast.error('Veuillez sélectionner le produit offert');
      return;
    }
    if (data.get_discount_type === 'percentage' && (!data.get_discount_value || data.get_discount_value <= 0 || data.get_discount_value > 100)) {
      toast.error('Le pourcentage doit être entre 1 et 100');
      return;
    }

    setSaving(true);
    try {
      if (offerModal.isEdit) {
        await axios.put(`${API}/bogo/offers/${data.id}`, data);
        toast.success('Offre modifiée');
      } else {
        await axios.post(`${API}/bogo/offers`, data);
        toast.success('Offre créée');
      }
      setOfferModal({ open: false, data: null, isEdit: false });
      fetchOffers();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOffer = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/bogo/offers/${deleteModal.offerId}`);
      toast.success('Offre supprimée');
      setDeleteModal({ open: false, offerId: null, offerName: '' });
      fetchOffers();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la suppression'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleOffer = async (offerId, currentStatus) => {
    try {
      await axios.patch(`${API}/bogo/offers/${offerId}/toggle`, {});
      toast.success(currentStatus ? 'Offre désactivée' : 'Offre activée');
      fetchOffers();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getOfferSummary = (offer) => {
    const buyText = offer.buy_type === 'product' 
      ? offer.buy_product_name 
      : `catégorie "${offer.buy_category_name}"`;
    
    let getItemText = '';
    if (offer.get_type === 'same') {
      getItemText = offer.buy_type === 'product' ? offer.buy_product_name : 'le même';
    } else if (offer.get_type === 'product') {
      getItemText = offer.get_product_name;
    }
    
    const discountText = offer.get_discount_type === 'free' 
      ? 'offert' 
      : `-${offer.get_discount_value}%`;
    
    return {
      buy: `${offer.buy_quantity} ${buyText}`,
      get: `${offer.get_quantity} ${getItemText} ${discountText}`
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">X Achetés = Y Offert</h2>
          <p className="text-sm text-gray-500">Créez des offres promotionnelles sur la quantité</p>
        </div>
        <Button 
          onClick={() => openOfferModal()}
          className="bg-purple-500 hover:bg-purple-600 rounded-xl"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle offre
        </Button>
      </div>

      {/* Offers List */}
      {offers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-semibold text-gray-900 mb-2">Aucune offre</h3>
          <p className="text-gray-500 mb-4">Créez votre première offre "X achetés = Y offert"</p>
            <Button onClick={() => openOfferModal()} variant="outline" className="rounded-xl">
              <Plus className="w-4 h-4 mr-2" />
              Créer une offre
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {offers.map(offer => {
              const summary = getOfferSummary(offer);
              return (
                <div 
                  key={offer.id}
                  className={`bg-white rounded-2xl border p-5 ${
                    offer.is_active ? 'border-purple-200' : 'border-gray-200 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        offer.is_active ? 'bg-purple-100' : 'bg-gray-100'
                      }`}>
                        <Gift className={`w-6 h-6 ${offer.is_active ? 'text-purple-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{offer.name}</h3>
                        {offer.description && (
                          <p className="text-sm text-gray-500">{offer.description}</p>
                        )}
                      </div>
                    </div>
                    <Switch
                      checked={offer.is_active}
                      onCheckedChange={() => handleToggleOffer(offer.id, offer.is_active)}
                    />
                  </div>
                  
                  {/* Offer Details */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">{summary.buy}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-purple-500" />
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-emerald-500" />
                        <span className="font-medium text-emerald-600">{summary.get}</span>
                      </div>
                    </div>
                  </div>

                  {/* Restrictions */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {offer.max_uses_per_order > 1 && (
                      <Badge variant="secondary" className="text-xs">
                        Max {offer.max_uses_per_order}x par commande
                      </Badge>
                    )}
                    {offer.available_days && offer.available_days.length < 7 && (
                      <Badge variant="secondary" className="text-xs">
                        <Calendar className="w-3 h-3 mr-1" />
                        Jours limités
                      </Badge>
                    )}
                    {(offer.start_time || offer.end_time) && (
                      <Badge variant="secondary" className="text-xs">
                        <Clock className="w-3 h-3 mr-1" />
                        {offer.start_time || '00:00'} - {offer.end_time || '23:59'}
                      </Badge>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-2">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => openOfferModal(offer)}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Modifier
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => setDeleteModal({ 
                        open: true, 
                        offerId: offer.id, 
                        offerName: offer.name 
                      })}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Supprimer
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {/* Offer Modal */}
      <Dialog open={offerModal.open} onOpenChange={(open) => !open && setOfferModal({ open: false, data: null, isEdit: false })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-purple-500" />
              {offerModal.isEdit ? "Modifier l'offre" : 'Nouvelle offre'}
            </DialogTitle>
          </DialogHeader>
          
          {offerModal.data && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label>Nom de l'offre *</Label>
                  <Input
                    value={offerModal.data.name}
                    onChange={(e) => setOfferModal({
                      ...offerModal,
                      data: { ...offerModal.data, name: e.target.value }
                    })}
                    placeholder="Ex: 3 Burgers = 1 Offert"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Description (optionnelle)</Label>
                  <Input
                    value={offerModal.data.description || ''}
                    onChange={(e) => setOfferModal({
                      ...offerModal,
                      data: { ...offerModal.data, description: e.target.value }
                    })}
                    placeholder="Description de l'offre..."
                    className="mt-1.5"
                  />
                </div>
              </div>

              {/* Buy Condition */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Condition d'achat
                </h4>
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setOfferModal({
                      ...offerModal,
                      data: { ...offerModal.data, buy_type: 'product', buy_category_id: '', buy_category_name: '' }
                    })}
                    className={`p-3 rounded-xl border-2 text-center ${
                      offerModal.data.buy_type === 'product'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <p className="font-medium">Produit spécifique</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOfferModal({
                      ...offerModal,
                      data: { ...offerModal.data, buy_type: 'category', buy_product_id: '', buy_product_name: '' }
                    })}
                    className={`p-3 rounded-xl border-2 text-center ${
                      offerModal.data.buy_type === 'category'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <p className="font-medium">Catégorie entière</p>
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {offerModal.data.buy_type === 'product' ? (
                    <div>
                      <Label>Produit à acheter</Label>
                      <Select
                        value={offerModal.data.buy_product_id || ''}
                        onValueChange={handleBuyProductSelect}
                      >
                        <SelectTrigger className="mt-1.5">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          {dishes.map(dish => (
                            <SelectItem key={dish.id} value={dish.id}>
                              {dish.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Label>Catégorie</Label>
                      <Select
                        value={offerModal.data.buy_category_id || ''}
                        onValueChange={handleBuyCategorySelect}
                      >
                        <SelectTrigger className="mt-1.5">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label>Quantité à acheter</Label>
                    <Input
                      type="number"
                      min="1"
                      value={offerModal.data.buy_quantity}
                      onChange={(e) => setOfferModal({
                        ...offerModal,
                        data: { ...offerModal.data, buy_quantity: parseInt(e.target.value) || 1 }
                      })}
                      className="mt-1.5"
                    />
                  </div>
                </div>
              </div>

              {/* Get Reward */}
              <div className="bg-emerald-50 rounded-xl p-4 space-y-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Gift className="w-4 h-4 text-emerald-600" />
                  Récompense
                </h4>
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setOfferModal({
                      ...offerModal,
                      data: { ...offerModal.data, get_type: 'same', get_product_id: '', get_product_name: '' }
                    })}
                    className={`p-3 rounded-xl border-2 text-center ${
                      offerModal.data.get_type === 'same'
                        ? 'border-emerald-500 bg-white'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <p className="font-medium">Même produit</p>
                    <p className="text-xs text-gray-500">Le produit acheté</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOfferModal({
                      ...offerModal,
                      data: { ...offerModal.data, get_type: 'product' }
                    })}
                    className={`p-3 rounded-xl border-2 text-center ${
                      offerModal.data.get_type === 'product'
                        ? 'border-emerald-500 bg-white'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <p className="font-medium">Autre produit</p>
                    <p className="text-xs text-gray-500">Un produit différent</p>
                  </button>
                </div>

                {offerModal.data.get_type === 'product' && (
                  <div>
                    <Label>Produit offert</Label>
                    <Select
                      value={offerModal.data.get_product_id || ''}
                      onValueChange={handleGetProductSelect}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Sélectionner le produit offert..." />
                      </SelectTrigger>
                      <SelectContent>
                        {dishes.map(dish => (
                          <SelectItem key={dish.id} value={dish.id}>
                            {dish.name} ({dish.price?.toFixed(2)}€)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Quantité offerte</Label>
                    <Input
                      type="number"
                      min="1"
                      value={offerModal.data.get_quantity}
                      onChange={(e) => setOfferModal({
                        ...offerModal,
                        data: { ...offerModal.data, get_quantity: parseInt(e.target.value) || 1 }
                      })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label>Type de réduction</Label>
                    <Select
                      value={offerModal.data.get_discount_type}
                      onValueChange={(val) => setOfferModal({
                        ...offerModal,
                        data: { ...offerModal.data, get_discount_type: val }
                      })}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">100% offert (gratuit)</SelectItem>
                        <SelectItem value="percentage">Pourcentage de réduction</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {offerModal.data.get_discount_type === 'percentage' && (
                  <div className="w-1/2">
                    <Label>Pourcentage de réduction</Label>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={offerModal.data.get_discount_value || ''}
                        onChange={(e) => setOfferModal({
                          ...offerModal,
                          data: { ...offerModal.data, get_discount_value: parseInt(e.target.value) || null }
                        })}
                      />
                      <Percent className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                )}
              </div>

              {/* Restrictions */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Restrictions (optionnel)</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Max utilisations par commande</Label>
                    <Input
                      type="number"
                      min="1"
                      value={offerModal.data.max_uses_per_order}
                      onChange={(e) => setOfferModal({
                        ...offerModal,
                        data: { ...offerModal.data, max_uses_per_order: parseInt(e.target.value) || 1 }
                      })}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Heure de début</Label>
                    <Input
                      type="time"
                      value={offerModal.data.start_time || ''}
                      onChange={(e) => setOfferModal({
                        ...offerModal,
                        data: { ...offerModal.data, start_time: e.target.value || null }
                      })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label>Heure de fin</Label>
                    <Input
                      type="time"
                      value={offerModal.data.end_time || ''}
                      onChange={(e) => setOfferModal({
                        ...offerModal,
                        data: { ...offerModal.data, end_time: e.target.value || null }
                      })}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label>Jours disponibles</Label>
                  <p className="text-xs text-gray-500 mb-2">Laissez vide pour tous les jours</p>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OPTIONS.map(day => {
                      const isSelected = offerModal.data.available_days?.includes(day.value);
                      return (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => handleDayToggle(day.value)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            isSelected
                              ? 'bg-purple-100 text-purple-700 border border-purple-300'
                              : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                          }`}
                        >
                          {isSelected && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                          {day.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date de début</Label>
                    <Input
                      type="date"
                      value={offerModal.data.start_date || ''}
                      onChange={(e) => setOfferModal({
                        ...offerModal,
                        data: { ...offerModal.data, start_date: e.target.value || null }
                      })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label>Date de fin</Label>
                    <Input
                      type="date"
                      value={offerModal.data.end_date || ''}
                      onChange={(e) => setOfferModal({
                        ...offerModal,
                        data: { ...offerModal.data, end_date: e.target.value || null }
                      })}
                      className="mt-1.5"
                    />
                  </div>
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-medium text-gray-900">Offre active</p>
                  <p className="text-xs text-gray-500">L'offre sera appliquée automatiquement</p>
                </div>
                <Switch
                  checked={offerModal.data.is_active}
                  onCheckedChange={(checked) => setOfferModal({
                    ...offerModal,
                    data: { ...offerModal.data, is_active: checked }
                  })}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setOfferModal({ open: false, data: null, isEdit: false })}>
              Annuler
            </Button>
            <Button onClick={handleSaveOffer} disabled={saving} className="bg-purple-500 hover:bg-purple-600">
              {saving ? 'Sauvegarde...' : offerModal.isEdit ? 'Modifier' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, offerId: null, offerName: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer l'offre <strong>"{deleteModal.offerName}"</strong> ?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, offerId: null, offerName: '' })}>
              Annuler
            </Button>
            <Button onClick={handleDeleteOffer} disabled={saving} className="bg-red-500 hover:bg-red-600">
              {saving ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
